import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {t} from 'i18next';
import {
  GridRowClassNameParams,
  GridRowParams,
  useGridApiRef,
} from '@mui/x-data-grid';
import {debounce} from 'lodash';
import AurDataGrid from '../../../common/aur-data-grid/AurDataGrid';
import custodianReportTableColumnDef, {
  renderTableCellData,
} from './CustodianReportTableColumnDef';
import {CustodianType} from '../ByCustodianTab';
import {
  TABLE_PAGE_SIZE_OPTION,
  TABLE_PER_PAGE_DEFAULT_NO_ROW,
} from '../../../../constants/constants';
import {convertToDecimalPlace} from '../../../../utils/inTakeAuditReportUtils';
import AurDataGridLoadingOverlay from '../../../common/aur-data-grid-loading-overlay/AurDataGridLoadingOverlay';
import AurDataGridErrorOverlay from '../../../common/aur-data-grid-error-overlay/AurDataGridErrorOverlay';

interface CustodianReportDataTableProps {
  custodianData: CustodianType[];
  onCustodianRowHoverEvent?: (event: CustodianType | null) => void; // callback function to send hovered row data to update on progress chart
  onPagePropsChange?: (itemsPerPage: number, currentPage: number) => void;
  totalRecordsCount?: number;
  onRefresh?: () => void;
  isChartFailed?: boolean;
  isChartLoading?: boolean;
  size?: number;
  itemPerPage: number;
}

const CustodianReportDataTable: React.FC<CustodianReportDataTableProps> = ({
  custodianData,
  onCustodianRowHoverEvent,
  totalRecordsCount = 0,
  onPagePropsChange,
  onRefresh,
  isChartFailed,
  isChartLoading,
  size,
  itemPerPage,
}) => {
  const gridAPIRef = useGridApiRef();
  const [itemsPerPage, setItemsPerPage] = useState<number>(itemPerPage);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);

  useEffect(() => {
    setItemsPerPage(itemPerPage);
  }, [itemPerPage]);

  useEffect(() => {
    onPagePropsChange ? onPagePropsChange(itemsPerPage, currentPage) : '';
    setSelectedRowId(null);
    debounceHoverEvent(null);
  }, [itemsPerPage, currentPage]);

  // debounce hover event of table row to send back to subscriber
  const debounceHoverEvent = useCallback(
    debounce((param: CustodianType | null) => {
      onCustodianRowHoverEvent && onCustodianRowHoverEvent(param);
    }, 300),
    [onCustodianRowHoverEvent]
  );

  const handleRowClick = useCallback(
    (event: GridRowParams) => {
      const clickedRowId = event.row.id;

      if (clickedRowId === selectedRowId) {
        setSelectedRowId(null);
        debounceHoverEvent(null);
      } else {
        setSelectedRowId(clickedRowId);
        const rowData = custodianData.find((row) => row.id === clickedRowId);
        debounceHoverEvent(rowData || null);
      }
    },
    [selectedRowId, custodianData]
  );

  const totalRow = useMemo(() => {
    const totals: {[key: string]: number | string} = custodianData?.reduce(
      (acc, row) => {
        acc.emailCompressed += +row.emailCompressed;
        acc.externallyProcessed += +row.externallyProcessed;
        acc.otherData += +row.otherData;
        acc.subTotal += +row.subTotal;
        return acc;
      },
      {
        custodianName: t('reports.intakeAudit.custodianTable.totals'),
        emailCompressed: 0,
        externallyProcessed: 0,
        otherData: 0,
        subTotal: 0,
        id: 'totals-row',
        receivedDate: '',
      }
    );

    return totals;
  }, [custodianData, currentPage, itemsPerPage]);

  const renderTotalRowComponent = useCallback(() => {
    return (
      <div className="totals-row-container" data-testid="total-custodian-row">
        {custodianReportTableColumnDef?.map((column, index) => (
          <div
            style={{width: `${(column.flex ?? 0) * 100}%`}}
            className={'table-data-cell'}
            key={`${column.field}-${index}`}
            aria-label={
              index !== 0
                ? `${t('common.total')} ${totalRow?.[column.field]} ${column?.field} ${t('common.compressedGB')}`
                : t('aria.rowNameTotals')
            }
            data-testid={`${column?.field}-total`}
          >
            {renderTableCellData(
              column.field === 'custodianName'
                ? totalRow?.[column?.field as string]
                : convertToDecimalPlace(
                    totalRow?.[column?.field as string].toString()
                  ),
              'label1_size text-color'
            )}
          </div>
        ))}
      </div>
    );
  }, [totalRow]);
  const getRowClassName = (params: GridRowClassNameParams) => {
    return params.row.id === selectedRowId ? 'selected-row' : '';
  };

  return (
    <AurDataGrid
      className="custodian-data-table"
      rows={custodianData}
      columns={custodianReportTableColumnDef}
      entityType={t('common.custodian')}
      currentPage={currentPage}
      visibleToolTip={true}
      itemsPerPage={itemsPerPage}
      setItemsPerPage={setItemsPerPage}
      setCurrentPage={setCurrentPage}
      checkboxSelection={false}
      pageSizeOptions={TABLE_PAGE_SIZE_OPTION}
      footerChildComponent={renderTotalRowComponent()}
      columnHeaderHeight={64}
      disableRowSelectionOnClick
      // need to use ID once we get it from API
      getRowId={(row) => row.id}
      getRowHeight={() => 56}
      apiRef={gridAPIRef}
      totalRowCount={totalRecordsCount}
      onRowClick={handleRowClick}
      getRowClassName={getRowClassName}
      minItemForPagination={TABLE_PER_PAGE_DEFAULT_NO_ROW}
      enableRowClickOnEnterPress
      containerHeight={size}
      noRowsOverlay={
        isChartLoading
          ? AurDataGridLoadingOverlay
          : isChartFailed
            ? AurDataGridErrorOverlay
            : undefined
      }
      onRefresh={onRefresh}
      trackPaginationActionName={t('gaTracking.global.reportPagination')}
    />
  );
};

export default CustodianReportDataTable;
